import {
  BadgeDollarSign,
  CircleCheck,
  CircleX,
  CreditCard,
  Database,
  Inbox,
  LifeBuoy,
  type LucideIcon,
  Mail,
  ServerCrash,
  Sparkles,
  TrendingUp,
  TriangleAlert,
  UserPlus,
} from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { BarList, Funnel, MovementChart } from '@/components/redesign/charts'
import { CountryCode, FeedItem, KpiTile, Panel, Pipe, SectionHead } from '@/components/redesign/bits'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { useDashboard } from '@/app/shell'
import { useMe } from '@/features/auth/api'
import type { AttentionItem, Briefing } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { formatAgo, formatMinutes, formatMoney, numberFmt } from '@/lib/format'
import { type BriefingRange, useBriefing } from './api'

const THIRTY_DAYS = { value: '30d' as BriefingRange, label: '30 days', text: 'last 30 days' }
const RANGES: { value: BriefingRange; label: string; text: string }[] = [
  { value: '7d', label: '7 days', text: 'last 7 days' },
  THIRTY_DAYS,
  { value: '90d', label: '90 days', text: 'last 90 days' },
  { value: '12m', label: '12 months', text: 'last 12 months' },
]

/** Each attention item gets a kicker and an icon in the "Needs you now" cards. */
const ATTENTION: Record<string, { kicker: string; icon: LucideIcon }> = {
  tickets_slow: { kicker: 'Support', icon: Inbox },
  tickets_unassigned: { kicker: 'Support', icon: Inbox },
  tickets_mine: { kicker: 'Assigned to you', icon: Inbox },
  enquiries: { kicker: 'Possible customers', icon: Sparkles },
  billing_events: { kicker: 'Billing', icon: TriangleAlert },
  past_due: { kicker: 'Payment failing', icon: CreditCard },
  failed_jobs: { kicker: 'Background work', icon: ServerCrash },
  free_budget: { kicker: 'Data budget', icon: Database },
  operator_email: { kicker: 'Setup', icon: Mail },
  billing_off: { kicker: 'Billing', icon: CreditCard },
}

const FEED_ICON: Record<string, LucideIcon> = {
  signup: UserPlus,
  paid: BadgeDollarSign,
  addon: TrendingUp,
  cancel: CircleX,
  failed: CreditCard,
  ticket: LifeBuoy,
}

/** Where sign-ups came from: the ad platforms first in their chart colours. */
const channelColor = (label: string) =>
  label === 'Google Ads'
    ? 'var(--viz-1)'
    : label === 'Meta ads'
      ? 'var(--viz-2)'
      : label === 'No campaign tags'
        ? 'var(--viz-other)'
        : 'var(--viz-4)'

const series = (values: (number | null)[]) => values.map((v) => v ?? 0)

function greeting() {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

/**
 * The control panel's first page (redesign of 2026-10-16): what needs someone now, the pulse of
 * the business against the previous period, where recurring revenue moved this month, what's
 * happening, where sign-ups come from, the support queue and how far new workspaces get.
 */
export function DashboardPage() {
  const [params, setParams] = useSearchParams()
  const picked = RANGES.find((r) => r.value === params.get('range')) ?? THIRTY_DAYS
  const range = picked.value
  const rangeText = picked.text
  const me = useMe()
  const dashboard = useDashboard()
  const briefing = useBriefing(range)

  const firstName = (me.data?.name ?? '').split(' ')[0]
  const today = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(
    new Date(),
  )
  const attention = dashboard.data?.attention ?? []

  return (
    <>
      <header className="greet">
        <div>
          <div className="date">{today}</div>
          <h1>
            {greeting()}
            {firstName ? `, ${firstName}` : ''}
          </h1>
          {briefing.data && <p className="sub">{summary(attention.length, briefing.data)}</p>}
        </div>
        <div className="acts">
          <Segmented
            label="Period"
            value={range}
            onChange={(v) => setParams(v === '30d' ? {} : { range: v }, { replace: true })}
            options={RANGES.map((r) => ({ value: r.value, label: r.label }))}
          />
        </div>
      </header>

      <section className="sec" aria-labelledby="now-h">
        <div className="sec-h">
          <h2 id="now-h">Needs you now</h2>
          <p>Most urgent first. Each one opens where it’s handled.</p>
        </div>
        {dashboard.isPending ? (
          <Skeleton className="h-28 rounded-[14px]" />
        ) : attention.length === 0 ? (
          <div className="calm">
            <CircleCheck aria-hidden="true" />
            Nothing urgent. The queue and billing are in good shape.
          </div>
        ) : (
          <div className="now">
            {attention.map((item) => (
              <NeedsYou key={item.key} item={item} />
            ))}
          </div>
        )}
      </section>

      {briefing.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : briefing.isError ? (
        <div className="card">
          <ErrorState error={briefing.error} onRetry={() => void briefing.refetch()} />
        </div>
      ) : (
        <Body data={briefing.data} rangeText={rangeText} dim={briefing.isPlaceholderData} />
      )}
    </>
  )
}

function summary(needs: number, d: Briefing) {
  const parts = [
    needs === 0
      ? 'Nothing needs you right now.'
      : `${needs === 1 ? 'One thing needs' : `${numberFmt.format(needs)} things need`} you today.`,
  ]
  const { now, previous } = d.pulse.signups
  if (now != null && previous) {
    const change = Math.round(((now - previous) / previous) * 100)
    if (change !== 0)
      parts.push(`Sign-ups are ${change > 0 ? 'up' : 'down'} ${Math.abs(change)}% on the previous period.`)
  }
  if (d.pulse.mrr.now != null) parts.push(`Recurring revenue is ${formatMoney(d.pulse.mrr.now)} a month.`)
  return parts.join(' ')
}

function NeedsYou({ item }: { item: AttentionItem }) {
  const navigate = useNavigate()
  const meta = ATTENTION[item.key] ?? { kicker: 'Needs a look', icon: TriangleAlert }
  const Icon = meta.icon
  const tone = item.severity === 'urgent' ? 'bad' : item.severity === 'warning' ? 'warn' : ''
  const body = (
    <>
      <span className={cn('nw-ic', tone)}>
        <Icon aria-hidden="true" />
      </span>
      <span className="nw-b">
        <span className="nw-k">{meta.kicker}</span>
        <span className="nw-t">{item.title}</span>
        <span className="nw-s">{item.detail}</span>
      </span>
    </>
  )
  const url = item.url
  return url ? (
    <button type="button" className="nw" onClick={() => void navigate(url)}>
      {body}
    </button>
  ) : (
    <div className="nw" style={{ cursor: 'default' }}>
      {body}
    </div>
  )
}

function Body({ data, rangeText, dim }: { data: Briefing; rangeText: string; dim: boolean }) {
  const p = data.pulse
  const m = data.movement
  const s = data.support
  const monthStart = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(
    new Date(`${m.from}T00:00:00`),
  )
  const compare = `vs the previous ${rangeText.replace('last ', '')}`

  return (
    <div className={cn('sec transition-opacity', dim && 'opacity-60')} style={{ gap: 26 }}>
      <section className="sec" aria-labelledby="pulse-h">
        <SectionHead
          title={<span id="pulse-h">Pulse</span>}
          small={rangeText}
          extra={<Link to="/sales">Revenue details</Link>}
        />
        <div className="kpis">
          <KpiTile
            label="Monthly recurring revenue"
            value={p.mrr.now != null ? formatMoney(p.mrr.now) : '—'}
            now={p.mrr.now}
            previous={p.mrr.previous}
            compare={compare}
            spark={series(p.mrr.series)}
            to="/sales"
          />
          <KpiTile
            label="New sign-ups"
            value={numberFmt.format(p.signups.now ?? 0)}
            now={p.signups.now}
            previous={p.signups.previous}
            compare={compare}
            spark={series(p.signups.series)}
            to="/audience"
          />
          <KpiTile
            label="Activation"
            value={p.activation.now != null ? `${Math.round(p.activation.now)}%` : '—'}
            now={p.activation.now}
            previous={p.activation.previous}
            points
            compare={p.activation.now != null ? compare : 'no workspaces a week old yet'}
            spark={series(p.activation.series)}
            to="/audience"
            hint="Verified a website and finished an audit within 7 days of signing up"
          />
          <KpiTile
            label="Paying customers"
            value={numberFmt.format(p.paying.now ?? 0)}
            now={p.paying.now}
            previous={p.paying.previous}
            compare={compare}
            spark={series(p.paying.series)}
            to="/subscriptions"
          />
          <KpiTile
            label="Revenue churn"
            value={p.churn.now != null ? `${p.churn.now.toFixed(1)}%` : '—'}
            now={p.churn.now}
            previous={p.churn.previous}
            invert
            points
            compare="lower is better"
            spark={series(p.churn.series)}
            sparkColor="var(--viz-3)"
            to="/sales"
          />
          <KpiTile
            label="First reply, median"
            value={p.first_reply.now != null ? formatMinutes(p.first_reply.now) : '—'}
            now={p.first_reply.now}
            previous={p.first_reply.previous}
            invert
            compare={p.first_reply.now != null ? 'faster is better' : 'no replies in this period'}
            spark={series(p.first_reply.series)}
            sparkColor="var(--viz-4)"
            to="/tickets"
          />
        </div>
      </section>

      <div className="split">
        <Panel
          title="Where this month’s recurring revenue moved"
          sub={`From ${monthStart} to today: new plans and upgrades in, downgrades and cancellations out.`}
          extra={
            <Link className="linkbtn" to="/sales">
              See every change
            </Link>
          }
        >
          <MovementChart
            start={m.start}
            now={m.now}
            startLabel={monthStart}
            format={formatMoney}
            moves={[
              { label: 'New plans', value: m.new },
              { label: 'Upgrades', value: m.upgrades },
              { label: 'Downgrades', value: m.downgrades },
              { label: 'Cancelled', value: m.cancelled },
            ]}
          />
          {m.biggest.length > 0 && (
            <div className="moves">
              <div className="pop-l" style={{ padding: 0 }}>
                Biggest changes
              </div>
              {m.biggest.map((c, i) => (
                <div key={i} className="srow">
                  <span>
                    {c.workspace ? (
                      <Link to={`/customers/${c.workspace.id}`} style={{ fontWeight: 600 }}>
                        {c.workspace.name}
                      </Link>
                    ) : (
                      <b>A customer</b>
                    )}{' '}
                    <span style={{ color: 'var(--subtle)' }}>{c.what}</span>
                  </span>
                  <b className="tabular" style={{ color: c.value > 0 ? 'var(--ok)' : 'var(--bad)' }}>
                    {c.value > 0 ? '+' : '−'}
                    {formatMoney(Math.abs(c.value))}
                  </b>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel
          title="Happening now"
          sub="Sign-ups, payments and tickets as they come in"
          extra={
            <span className="chip lime">
              <span className="dot lime" />
              Live
            </span>
          }
        >
          {data.feed.length === 0 ? (
            <p className="empty-line">Nothing in the last two weeks yet.</p>
          ) : (
            <div className="feed">
              {data.feed.map((e, i) => {
                const Icon = FEED_ICON[e.kind] ?? Sparkles
                const name = e.workspace ? <Link to={`/customers/${e.workspace.id}`}>{e.name}</Link> : e.name
                return (
                  <FeedItem
                    key={i}
                    icon={<Icon aria-hidden="true" />}
                    tone={e.tone}
                    when={formatAgo(e.at)}
                    meta={
                      e.country || e.meta.length ? (
                        <>
                          {e.country && <CountryCode code={e.country} />}
                          {e.meta.map((x, j) => (
                            <span key={j} style={{ display: 'contents' }}>
                              {(j > 0 || e.country) && <span aria-hidden="true">·</span>}
                              <span>{x}</span>
                            </span>
                          ))}
                        </>
                      ) : undefined
                    }
                  >
                    <b>{name}</b> {e.url ? <Link to={e.url}>{e.text}</Link> : e.text}
                  </FeedItem>
                )
              })}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid2">
        <Panel
          title="Where sign-ups came from"
          sub="By the campaign tags of the link each person signed up through"
          extra={
            <Link className="linkbtn" to="/audience">
              Audience
            </Link>
          }
        >
          {data.channels.length === 0 ? (
            <p className="empty-line">No sign-ups in this period.</p>
          ) : (
            <BarList
              rows={data.channels.map((c) => ({
                key: c.key,
                label: c.label,
                value: c.count,
                color: channelColor(c.label),
                lead: (
                  <i
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: channelColor(c.label),
                      display: 'block',
                    }}
                  />
                ),
              }))}
            />
          )}
          <p style={{ fontSize: 12.5, color: 'var(--subtle)', marginTop: 12 }}>
            Sign-ups keep their campaign since October 16, 2026; earlier ones and links without tags show as
            “No campaign tags”.
          </p>
        </Panel>
        <Panel title="Support pulse" sub="The queue right now and this week">
          <SupportStrip support={s} />
          {s.by_category.length > 0 && (
            <>
              <BarList
                rows={s.by_category.map((c) => ({
                  key: c.key,
                  label: c.label,
                  value: c.count,
                  color: 'var(--viz-4)',
                }))}
              />
              <p style={{ fontSize: 12.5, color: 'var(--subtle)', marginTop: 12 }}>
                Tickets by topic, last 30 days. A topic that keeps growing usually means a help article is
                missing.
              </p>
            </>
          )}
        </Panel>
      </div>

      <Panel
        title="From sign-up to paying"
        sub={`Workspaces made in the ${rangeText}, and how far each got`}
        extra={
          <Link className="linkbtn" to="/audience">
            Open Audience
          </Link>
        }
      >
        {data.funnel[0]?.count ? (
          <Funnel steps={data.funnel} />
        ) : (
          <p className="empty-line">No new workspaces in this period.</p>
        )}
      </Panel>
    </div>
  )
}

function SupportStrip({ support }: { support: Briefing['support'] }) {
  const navigate = useNavigate()
  const go = (key: string | null) => {
    if (key === 'open') void navigate('/tickets?status=open')
    else if (key === 'unassigned') void navigate('/tickets?status=open&assigned=unassigned')
    else if (key === 'waiting') void navigate('/tickets?status=answered')
    else if (key === 'resolved') void navigate('/tickets?status=resolved')
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <Pipe
        items={[
          { key: 'open', label: 'Open', count: support.open, dot: 'warn' },
          { key: 'unassigned', label: 'Nobody on it', count: support.unassigned, dot: 'bad' },
          { key: 'waiting', label: 'Waiting on them', count: support.waiting },
          { key: 'resolved', label: 'Resolved, 7 days', count: support.resolved_7d, dot: 'ok' },
        ]}
        onChange={go}
      />
    </div>
  )
}
